import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { CardiacFormField } from './formField'

/**
 * Cardiac subsection adapter
 * Handles cardiovascular assessment and heart sounds (4 fields)
 */
export class CardiacAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cardiac assessment fields
    const cardiacStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_WNL
    )?.at(0)

    const cardiacAbnormalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_FACTORS
    )

    const heartSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_HEART_SOUNDS
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [CardiacFormField.CARDIAC_CARDIOVASCULAR_WNL]: cardiacStatus,
      [CardiacFormField.CARDIAC_CARDIOVASCULAR_FACTORS]: cardiacAbnormalFactors,
      [CardiacFormField.CARDIAC_HEART_SOUNDS]: heartSounds,
      [CardiacFormField.CARDIAC_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_WNL,
        value: this.getFormDataField(CardiacFormField.CARDIAC_CARDIOVASCULAR_WNL)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_HEART_SOUNDS,
        value: this.getFormDataField(CardiacFormField.CARDIAC_HEART_SOUNDS)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_SYSTEM_SUMMARY,
        value: this.getFormDataField(CardiacFormField.CARDIAC_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add cardiovascular factors if they exist (they are conditionally rendered when status === 'Abnormal')
    if (this.formDataFieldExists(CardiacFormField.CARDIAC_CARDIOVASCULAR_FACTORS)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_FACTORS,
        value: this.getFormDataField(CardiacFormField.CARDIAC_CARDIOVASCULAR_FACTORS)
      })
    }

    return mutations
  }
}
